import { ReceiptPriceResult } from './PricingHistory';
import { useState } from 'react';
import {
  parseOpeningQtyHundredths,
  parseOpeningCostCents,
  type PoFulfillment,
  quickReceiveInput,
} from '@servwell/shared';
import { fieldErrorOf } from '../../lib/field-error';
import { useOnline } from '../ui/useConnection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import {
  StaleNotice,
  UnresolvedState,
  shownState,
  refreshFailed,
} from '../ui/QueryStates';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import { money } from '../money';
import type { OnHandItem, StockLocation } from './types';
import { IconInventory, IconPlus, IconTrash } from '../icons';

import {
  usePoReceiptWrite,
  useReceiptCompletion,
  type PoReceiptWrite,
} from './usePoReceiptWrite';

type Vendor = { id: string; name: string };
export type PoEditInitial = {
  purchaseOrderId: string;
  expectedRevision: string;
  vendorId: string;
  vendorName: string;
  locationId: string;
  locationName: string | null;
  expectedDate: string | null;
  memo: string | null;
  fulfillment: PoFulfillment;
  subtotalCents: number;
  lines: {
    itemId: string;
    name: string;
    vendorPartNumber: string | null;
    orderedHundredths: number;
    unitCostCents: number;
  }[];
};

type Draft = { itemId: string; qty: string; cost: string; vendorPartNumber?: string };

const blank = (): Draft[] => [{ itemId: '', qty: '', cost: '' }];
const parseQty = (s: string) => {
  const n = Number(s.trim());
  return s.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
};
const parseCostCents = (s: string) => {
  const n = Number(s.trim());
  return s.trim() !== '' && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

/**
 * One builder, two jobs (PRD v2 §6.3):
 *
 *  - `order` — a planned PO that starts as a draft and gets received later.
 *  - `counter` — the supply-house run. Already bought, already in the van; it saves as a
 *    purchase order that is born closed and fully received, so a run nobody planned still
 *    shows up in spend-by-vendor.
 *
 * They share a form because they are the same information; only the verb differs. A
 * counter run requires a price (you have the receipt in your hand), an order doesn't —
 * it falls back to what the shop paid last time.
 */
export function PoBuilderSheet(
  props: Omit<Parameters<typeof PoBuilderEditor>[0], 'receipt'>,
) {
  const [completed, setCompleted] = useState<{ owner: string; id: string } | null>(null);
  const receipt = usePoReceiptWrite((answer) => {
    setCompleted({ owner: receipt.owner, id: answer.purchaseOrder.id });
    props.onCreated(answer.purchaseOrder.id);
  });
  const locator = useReceiptCompletion(completed?.id ?? '');
  if (props.mode === 'counter' && completed?.owner === receipt.owner)
    return (
      <Sheet open onClose={props.onClose} title="Counter run recorded">
        <ReceiptPriceResult locator={locator} />
        <Button className="min-h-11" onClick={() => props.onCreated(completed.id)}>
          Open recorded receipt
        </Button>
      </Sheet>
    );
  if (props.mode === 'counter' && receipt.foreignPending)
    return (
      <Sheet open onClose={() => {}} title="Receipt recovery">
        <ReceiptFeedback write={receipt} />
      </Sheet>
    );
  return <PoBuilderEditor key={receipt.owner} {...props} receipt={receipt} />;
}
export function ReceiptFeedback({ write }: { write: PoReceiptWrite }) {
  if (write.foreignPending)
    return (
      <Notice tone="info">
        An unresolved receipt belongs to another purchasing session. Return to that
        session to finish recovery.
      </Notice>
    );
  const a = write.attempt;
  if (!a) return null;
  return (
    <div role="status" data-receipt-surface className="space-y-2 text-sm">
      <Notice tone="info">
        {a.accepted
          ? 'Accepted. Confirming the saved receipt…'
          : a.phase === 'writing'
            ? 'Recording receipt…'
            : a.phase === 'reconcile'
              ? 'Office reconciliation is required. The original receipt attempt is retained.'
              : 'Resolve this receipt before changing or closing the form.'}
      </Notice>
      {a.error ? <p className="break-words">{a.error}</p> : null}
      {a.phase === 'retry' ? (
        <Button className="min-h-11" onClick={write.retry}>
          Retry same receipt
        </Button>
      ) : null}
      {a.phase === 'review' ? (
        <Button className="min-h-11" onClick={() => void write.review()}>
          Refresh and review receipt
        </Button>
      ) : null}
      {a.phase === 'read_failed' ? (
        <Button className="min-h-11" onClick={() => void write.refresh()}>
          Retry receipt refresh
        </Button>
      ) : null}
    </div>
  );
}
function PoBuilderEditor({
  mode,
  items,
  locations,
  vendors,
  onClose,
  onCreated,
  initial,
  onReload,
  receipt,
}: {
  receipt: PoReceiptWrite;
  mode: 'order' | 'counter' | 'edit';
  initial?: PoEditInitial;
  onReload?: () => Promise<void>;
  items: OnHandItem[];
  locations: StockLocation[];
  vendors: Vendor[];
  onClose: () => void;
  onCreated: (purchaseOrderId: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const counter = mode === 'counter';
  const editing = mode === 'edit';
  const online = useOnline();
  const frozen =
    receipt.attempt?.action === 'counter' ? receipt.attempt.input : undefined;
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState<string | null>(null);

  const [vendorId, setVendorId] = useState(
    frozen?.vendorId ?? initial?.vendorId ?? vendors[0]?.id ?? '',
  );
  const [locationId, setLocationId] = useState(
    frozen?.locationId ?? initial?.locationId ?? locations[0]?.id ?? '',
  );
  const [expectedDate, setExpectedDate] = useState(initial?.expectedDate ?? '');
  const [fulfillment, setFulfillment] = useState<PoFulfillment | null>(
    initial?.fulfillment ?? null,
  );
  const [vendorChanged, setVendorChanged] = useState(false);
  const [memo, setMemo] = useState(frozen?.memo ?? initial?.memo ?? '');
  const [lines, setLines] = useState<Draft[]>(
    () =>
      frozen?.lines.map((l) => ({
        itemId: l.itemId,
        qty: String(l.qty),
        cost: (l.unitCostCents / 100).toFixed(2),
      })) ??
      initial?.lines.map((l) => ({
        itemId: l.itemId,
        vendorPartNumber: l.vendorPartNumber ?? '',
        qty: String(l.orderedHundredths / 100),
        cost: (l.unitCostCents / 100).toFixed(2),
      })) ??
      blank(),
  );

  const defaults = useQuery({
    ...trpc.purchasing.draftDefaults.queryOptions({
      vendorId,
      locationId,
      itemIds: lines.map((l) => l.itemId).filter(Boolean),
    }),
    enabled: !counter && Boolean(vendorId && locationId),
  });
  const defaultsState = shownState(defaults, defaults.data);
  const defaultsStale = refreshFailed(defaults, defaults.data);
  const resolvedFulfillment = fulfillment ?? defaults.data?.fulfillment;
  const defaultsReady = counter || (defaultsState === 'ready' && !defaultsStale);
  const shownVendorPartNumber = (line: Draft) =>
    line.vendorPartNumber ??
    defaults.data?.parts.find((p) => p.itemId === line.itemId)?.vendorPartNumber ??
    '';

  const done = (id: string) => {
    void qc.invalidateQueries();
    onCreated(id);
  };
  const create = useMutation(
    trpc.purchasing.create.mutationOptions({ onSuccess: (po) => done(po.id) }),
  );
  const edit = useMutation(
    trpc.purchasing.edit.mutationOptions({ onSuccess: (po) => done(po.id) }),
  );
  const busy =
    create.isPending || edit.isPending || reloading || (counter && receipt.locked);
  const error = edit.error ?? create.error;

  const patch = (i: number, next: Partial<Draft>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...next } : l)));

  const built = lines
    .map((l) => {
      const strictQty = editing ? parseOpeningQtyHundredths(l.qty) : null;
      const qty = editing
        ? strictQty !== null && strictQty > 0
          ? strictQty / 100
          : null
        : parseQty(l.qty);
      const cost = editing ? parseOpeningCostCents(l.cost) : parseCostCents(l.cost);
      if (!l.itemId || qty === null) return null;
      // A counter run has a receipt in hand, so the price is not optional there.
      if ((counter || editing) && cost === null) return null;
      // A reset/reselected existing item displays today's mapping. Omitting it in a
      // same-vendor edit would instead tell the server to preserve its OLD draft snapshot.
      // Untouched initial strings still travel unchanged and cannot re-learn an old mapping.
      const vendorPartNumber =
        l.vendorPartNumber ??
        (editing &&
        initial &&
        initial.vendorId === vendorId &&
        initial.lines.some((old) => old.itemId === l.itemId)
          ? shownVendorPartNumber(l)
          : undefined);
      return {
        itemId: l.itemId,
        qty,
        unitCostCents: cost,
        ...(vendorPartNumber !== undefined ? { vendorPartNumber } : {}),
      };
    })
    .filter(
      (
        l,
      ): l is {
        itemId: string;
        qty: number;
        unitCostCents: number | null;
        vendorPartNumber?: string;
      } => Boolean(l),
    );

  const total = editing
    ? (initial?.subtotalCents ?? 0)
    : built.reduce((sum, l) => {
        const fallback = items.find((i) => i.id === l.itemId)?.lastCostCents ?? 0;
        return sum + Math.round(l.qty * (l.unitCostCents ?? fallback));
      }, 0);

  const canSave =
    !busy &&
    defaultsReady &&
    Boolean(vendorId) &&
    Boolean(locationId) &&
    built.length > 0 &&
    (!editing || (online && Boolean(initial) && built.length === lines.length)) &&
    (!counter ||
      (online &&
        built.length === lines.length &&
        quickReceiveInput.safeParse({ vendorId, locationId, lines: built }).success));

  const save = () => {
    if (!canSave) return;
    if (editing && initial) {
      edit.mutate({
        purchaseOrderId: initial.purchaseOrderId,
        expectedRevision: initial.expectedRevision,
        ...(resolvedFulfillment ? { fulfillment: resolvedFulfillment } : {}),
        vendorId,
        locationId,
        expectedDate,
        memo,
        lines: built.map((l) => ({
          itemId: l.itemId,
          qty: l.qty,
          ...(l.vendorPartNumber !== undefined
            ? { vendorPartNumber: l.vendorPartNumber }
            : {}),
          unitCostCents: l.unitCostCents!,
        })),
        ...idem(),
      });
    } else if (counter) {
      receipt.begin({
        action: 'counter',
        input: {
          vendorId,
          locationId,
          memo: memo.trim() || undefined,
          lines: built.map((l) => ({
            itemId: l.itemId,
            qty: l.qty,
            ...(l.vendorPartNumber !== undefined
              ? { vendorPartNumber: l.vendorPartNumber }
              : {}),
            unitCostCents: l.unitCostCents!,
          })),
        },
      });
    } else {
      create.mutate({
        ...(resolvedFulfillment ? { fulfillment: resolvedFulfillment } : {}),
        vendorId,
        locationId,
        expectedDate: expectedDate || undefined,
        memo: memo.trim() || undefined,
        lines: built.map((l) => ({
          itemId: l.itemId,
          qty: l.qty,
          ...(l.vendorPartNumber !== undefined
            ? { vendorPartNumber: l.vendorPartNumber }
            : {}),
          ...(l.unitCostCents !== null ? { unitCostCents: l.unitCostCents } : {}),
        })),
        ...idem(),
      });
    }
  };

  return (
    <Sheet
      className={
        counter ? '[&_input]:min-h-11 [&_select]:min-h-11 [&_button]:min-h-11' : undefined
      }
      // Sonar-format width: a line-item builder is cramped at 420px (104).
      wide
      open
      onClose={() => {
        if (!((editing || counter) && busy)) onClose();
      }}
      title={
        editing
          ? 'Edit draft purchase order'
          : counter
            ? 'Bought at the counter'
            : 'New purchase order'
      }
      badge={
        <span className="text-sm text-muted-foreground">
          {editing
            ? 'Changes this draft; stock stays as recorded'
            : counter
              ? 'Already paid for — records stock without a prior-order price comparison'
              : 'Saves as a draft you can send'}
        </span>
      }
      footer={
        <Button className="w-full" disabled={!canSave} onClick={save}>
          {busy
            ? 'Saving…'
            : editing
              ? 'Save draft'
              : counter
                ? `Add ${money(total)} to stock`
                : `Create order · ${money(total)}`}
        </Button>
      }
    >
      {counter ? <ReceiptFeedback write={receipt} /> : null}
      <fieldset
        data-receipt-surface={counter ? true : undefined}
        className="min-w-0 space-y-4"
        disabled={(editing || counter) && busy}
      >
        {error ? (
          <Notice tone="danger">
            {editing ? fieldErrorOf(error.message) : error.message}
          </Notice>
        ) : null}
        {reloadError ? <Notice tone="danger">{reloadError}</Notice> : null}
        {editing ? (
          <p className="text-sm text-muted-foreground">
            Loaded total · {money(initial?.subtotalCents)}. Saving updates the total.
          </p>
        ) : null}
        {editing && !online ? (
          <Notice tone="warn">Go online to edit this order. Your inputs are kept.</Notice>
        ) : null}
        {editing && error && onReload ? (
          <Button
            variant="ghost"
            disabled={busy || !online}
            onClick={async () => {
              setReloadError(null);
              setReloading(true);
              try {
                await onReload();
              } catch (error) {
                setReloadError(
                  error instanceof Error
                    ? error.message
                    : 'Could not reload the saved draft.',
                );
              } finally {
                setReloading(false);
              }
            }}
          >
            Reload saved draft (replaces edits)
          </Button>
        ) : null}

        <Field label="Vendor">
          <select
            className={field}
            value={vendorId}
            aria-label="Vendor"
            onChange={(e) => {
              setVendorId(e.target.value);
              setLines((prev) =>
                prev.map((l) => ({ ...l, vendorPartNumber: undefined })),
              );
              setVendorChanged(true);
            }}
          >
            {editing && initial && !vendors.some((v) => v.id === initial.vendorId) ? (
              <option value={initial.vendorId}>{initial.vendorName} (unavailable)</option>
            ) : null}
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>

        {vendorChanged && !counter ? (
          <Notice tone="info">
            Vendor changed. Part numbers now use this vendor’s saved defaults; type a
            different number to replace one.
          </Notice>
        ) : null}
        <Field label={counter ? 'Goes into' : 'Stock destination'}>
          <select
            className={field}
            value={locationId}
            aria-label="Stock destination"
            onChange={(e) => {
              setLocationId(e.target.value);
              setFulfillment(null);
            }}
          >
            {editing && initial && !locations.some((l) => l.id === initial.locationId) ? (
              <option value={initial.locationId}>
                {initial.locationName || 'Saved location'} (unavailable)
              </option>
            ) : null}
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {'   '.repeat(l.depth)}
                {l.name}
              </option>
            ))}
          </select>
        </Field>

        {!counter ? (
          <>
            <StaleNotice
              show={refreshFailed(defaults, defaults.data)}
              onRetry={() => void defaults.refetch()}
            />
            {defaultsState !== 'ready' ? (
              <UnresolvedState
                state={defaultsState}
                what="Vendor defaults"
                icon={IconInventory}
                error={defaults.error}
                onRetry={() => void defaults.refetch()}
              />
            ) : null}
            <Field label="Fulfillment">
              <select
                className={field}
                aria-label="Fulfillment"
                value={fulfillment ?? defaults.data?.fulfillment ?? ''}
                onChange={(e) => setFulfillment(e.target.value as PoFulfillment)}
                disabled={!defaultsReady}
              >
                {!defaults.data && !fulfillment ? (
                  <option value="">Loading destination…</option>
                ) : null}
                <option value="deliver">Deliver to shop address</option>
                <option value="will_call">Will call by destination owner</option>
              </select>
            </Field>
          </>
        ) : null}
        {!counter ? (
          <Field label="Expected (optional)">
            <input
              className={field}
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
            />
          </Field>
        ) : null}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Parts
          </p>
          <ul className="space-y-2">
            {lines.map((l, i) => (
              <li key={i} className="space-y-1 rounded-[var(--radius)] bg-surface-2 p-2">
                {counter ? <p className="text-xs font-medium">Line {i + 1}</p> : null}
                <div className="flex min-w-0 gap-2">
                  <select
                    className={`${field} min-w-0 flex-1`}
                    value={l.itemId}
                    onChange={(e) =>
                      patch(i, { itemId: e.target.value, vendorPartNumber: undefined })
                    }
                    aria-label={`Part ${i + 1}`}
                  >
                    <option value="">Pick a part…</option>
                    {editing &&
                    l.itemId &&
                    !items.some((item) => item.id === l.itemId) ? (
                      <option value={l.itemId}>
                        {initial?.lines.find((line) => line.itemId === l.itemId)?.name ||
                          'Saved part'}{' '}
                        (unavailable)
                      </option>
                    ) : null}
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    aria-label={`Remove part ${i + 1}`}
                    disabled={lines.length === 1}
                    onClick={() => setLines((prev) => prev.filter((_, x) => x !== i))}
                    className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface hover:text-[var(--sw-danger-text)] disabled:opacity-40"
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
                {!counter ? (
                  <Field label="Vendor part number">
                    <input
                      className={`${field} min-w-0`}
                      value={shownVendorPartNumber(l)}
                      maxLength={120}
                      placeholder="Optional"
                      onChange={(e) => patch(i, { vendorPartNumber: e.target.value })}
                      aria-label={`Vendor part number ${i + 1}`}
                    />
                  </Field>
                ) : null}
                <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2">
                  <Field label="Quantity">
                    <input
                      className={`${field} min-w-0`}
                      value={l.qty}
                      onChange={(e) => patch(i, { qty: e.target.value })}
                      inputMode="decimal"
                      placeholder="Qty"
                      aria-label={`Quantity ${i + 1}`}
                    />
                  </Field>
                  <Field label="Unit cost">
                    <input
                      className={`${field} min-w-0`}
                      value={l.cost}
                      onChange={(e) => patch(i, { cost: e.target.value })}
                      inputMode="decimal"
                      aria-label={`Unit cost ${i + 1}`}
                      placeholder={
                        counter
                          ? 'Unit cost'
                          : (() => {
                              const last = items.find(
                                (x) => x.id === l.itemId,
                              )?.lastCostCents;
                              return last ? `Last paid ${money(last)}` : 'Unit cost';
                            })()
                      }
                    />
                  </Field>
                </div>
              </li>
            ))}
          </ul>
          <Button
            variant="ghost"
            className="mt-2"
            onClick={() =>
              setLines((prev) => [...prev, { itemId: '', qty: '', cost: '' }])
            }
          >
            <IconPlus size={14} />
            Add a part
          </Button>
        </div>

        {(editing || counter) && built.length !== lines.length ? (
          <p role="status" className="text-sm text-muted-foreground">
            Complete every part, positive quantity (up to two decimals), and unit cost
            (including 0) before saving. Incomplete rows are kept.
          </p>
        ) : null}
        <Field label="Note (optional)">
          <input
            className={field}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder={counter ? 'Counter run on the way to the Alvarez call' : ''}
          />
        </Field>

        {editing ? (
          <p className="text-xs text-muted-foreground">
            Every saved line needs an explicit cost. Cancel leaves the saved draft
            unchanged.
          </p>
        ) : counter ? (
          <p className="text-xs text-muted-foreground">
            This records the parts as received the moment you save, and updates what each
            one costs you.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Leave a price blank to use what you paid last time. Nothing hits stock until
            the parts actually arrive and you receive them.
          </p>
        )}
      </fieldset>
    </Sheet>
  );
}
